import React from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import MUIMenuList from '@mui/material/MenuList';

import { ValueContainerProps } from 'react-select/src/components/containers';
import { ControlProps } from 'react-select/src/components/Control';
import { MenuProps, NoticeProps } from 'react-select/src/components/Menu';
import { OptionProps } from 'react-select/src/components/Option';
import { PlaceholderProps } from 'react-select/src/components/Placeholder';
import { SingleValueProps } from 'react-select/src/components/SingleValue';
import { CommonProps } from 'react-select/src/types';
import { useTranslation } from 'services/localization/useTranslation';
import WebFont from 'webfontloader';
import { isFontLoaded, setFontLoaded } from './utils';

interface OptionType {
	label: string;
	value: string;
}

const NoOptionsMessage = (props: NoticeProps<OptionType, boolean>) => {
	const { t } = useTranslation();
	return (
		<MenuItem selected={false} disabled {...props.innerProps}>
			{t('account.general.fontSelector.noFontsFound', 'No fonts found')}
		</MenuItem>
	);
};

const LoadingMessage = (props: CommonProps<OptionType, boolean>) => {
	const { t } = useTranslation();
	return (
		<MenuItem selected={false} disabled>
			{t('common:states.loading') + '...'}
		</MenuItem>
	);
};

const InputComponent = React.forwardRef((props, ref) => {
	return <div {...props} />;
});

const Control = (props: ControlProps<OptionType, boolean>) => {
	const {
		children,
		innerProps,
		innerRef,
		selectProps: { TextFieldProps, error, textFieldLabel, isDisabled },
	} = props;

	return (
		<TextField
			disabled={isDisabled}
			fullWidth
			error={error}
			label={textFieldLabel}
			variant="outlined"
			InputProps={{
				inputComponent: InputComponent,
				inputProps: {
					ref: innerRef,
					children,
					...innerProps,
				},
			}}
			{...TextFieldProps}
		/>
	);
};

const Option = (props: OptionProps<OptionType, boolean>) => {
	const label = props.label;

	if (!isFontLoaded(label)) {
		WebFont.load({
			google: {
				families: [label],
			},
			classes: false,
			events: false,
		});
		setFontLoaded(label);
	}

	return (
		<MenuItem
			ref={props.innerRef}
			selected={props.isSelected}
			component="div"
			sx={{
				fontWeight: props.isSelected ? 500 : 400,
			}}
			{...props.innerProps}
		>
			<span style={{ fontFamily: label }}>{label}</span>
		</MenuItem>
	);
};

const IndicatorsContainer = () => null;

const Placeholder = (props: PlaceholderProps<OptionType, boolean>) => {
	return (
		<Typography color="secondary" className={props.selectProps.classes.placeholder}>
			{'Inter'}
		</Typography>
	);
};

const SingleValue = (props: SingleValueProps<OptionType>) => {
	const {
		selectProps: { classes, value },
	} = props;
	const label = value?.label;

	if (!!label) {
		if (!isFontLoaded(label)) {
			WebFont.load({
				google: {
					families: [label],
				},
				classes: false,
				events: false,
			});
			setFontLoaded(label);
		}
	}

	return props.children ? (
		<Typography style={{ fontFamily: value?.label }} classes={classes}>
			{props.children}
		</Typography>
	) : null;
};

const ValueContainer = (props: ValueContainerProps<OptionType, boolean>) => {
	return (
		<div className={props.selectProps.classes.valueContainer}>
			<div className={props.selectProps.classes.valueWrapper}>{props.children}</div>
		</div>
	);
};

const Menu = (props: MenuProps<OptionType, boolean>) => {
	return (
		<Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
			{props.children}
		</Paper>
	);
};

const MenuList = (props: MenuProps<OptionType, boolean>) => {
	return <MUIMenuList {...props.innerProps}>{props.children}</MUIMenuList>;
};

export const components = {
	Control,
	Menu,
	MenuList,
	NoOptionsMessage,
	Option,
	Placeholder,
	SingleValue,
	ValueContainer,
	IndicatorsContainer,
	LoadingMessage,
};
